import type { SupabaseClient } from '@supabase/supabase-js';

/**
 * The app's view of authentication: small, testable, and independent of Supabase's API shape.
 * Email one-time code is the route in this release (ADR-0016); Google sign-in follows the iPhone spike.
 */
export interface AuthUser {
  id: string;
  email: string;
}

export type AuthError = 'offline' | 'rate_limited' | 'invalid_email' | 'bad_code' | 'unknown';

export interface AuthPort {
  current(): Promise<AuthUser | null>;
  accessToken(): Promise<string | null>;
  sendCode(email: string): Promise<{ ok: true } | { ok: false; error: AuthError }>;
  verifyCode(email: string, code: string): Promise<{ ok: true; user: AuthUser } | { ok: false; error: AuthError }>;
  /** ADR-0004: a Google ID token plus the raw nonce whose hash Google signed. Absent when not configured. */
  signInWithGoogle?(idToken: string, rawNonce: string): Promise<{ ok: true; user: AuthUser } | { ok: false; error: AuthError }>;
  signOut(): Promise<void>;
  onChange(cb: (user: AuthUser | null) => void): () => void;
}

function classify(e: { message?: string | undefined; status?: number | undefined; code?: string | undefined } | null | undefined): AuthError {
  if (!e) return 'unknown';
  const m = (e.message ?? '').toLowerCase();
  if (e.status === 429 || /rate limit|too many|security purposes/.test(m)) return 'rate_limited';
  if (/fetch|network|failed to fetch|load failed/.test(m) || e.status === 0) return 'offline';
  if (/invalid.*email|email.*invalid|unable to validate email/.test(m)) return 'invalid_email';
  if (/token|otp|expired|invalid/.test(m) || e.code === 'otp_expired') return 'bad_code';
  return 'unknown';
}

export function supabaseAuth(client: SupabaseClient): AuthPort {
  const toUser = (u: { id: string; email?: string } | null | undefined): AuthUser | null => (u ? { id: u.id, email: u.email ?? '' } : null);
  return {
    async current() {
      const { data } = await client.auth.getSession(); // local: works offline
      return toUser(data.session?.user);
    },
    async accessToken() {
      const { data } = await client.auth.getSession();
      return data.session?.access_token ?? null;
    },
    async sendCode(email) {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return { ok: false, error: 'offline' };
      try {
        const { error } = await client.auth.signInWithOtp({ email, options: { shouldCreateUser: true } });
        return error ? { ok: false, error: classify(error) } : { ok: true };
      } catch (e) {
        return { ok: false, error: classify(e as Error) };
      }
    },
    async verifyCode(email, code) {
      try {
        const { data, error } = await client.auth.verifyOtp({ email, token: code, type: 'email' });
        const user = toUser(data.user);
        return error || !user ? { ok: false, error: error ? classify(error) : 'unknown' } : { ok: true, user };
      } catch (e) {
        return { ok: false, error: classify(e as Error) };
      }
    },
    async signInWithGoogle(idToken, rawNonce) {
      try {
        const { data, error } = await client.auth.signInWithIdToken({ provider: 'google', token: idToken, nonce: rawNonce });
        const user = toUser(data.user);
        return error || !user ? { ok: false, error: error ? classify(error) : 'unknown' } : { ok: true, user };
      } catch (e) {
        return { ok: false, error: classify(e as Error) };
      }
    },
    async signOut() {
      await client.auth.signOut({ scope: 'local' }).catch(() => undefined); // works offline
    },
    onChange(cb) {
      const { data } = client.auth.onAuthStateChange((_e, s) => cb(toUser(s?.user)));
      return () => data.subscription.unsubscribe();
    },
  };
}

export const AUTH_COPY: Record<AuthError, string> = {
  offline: "You're offline. You need a connection once to sign in. After that Repwise works offline.",
  rate_limited: 'Too many codes asked for. Wait a minute, then try again.',
  invalid_email: "That email address doesn't look right. Check it and try again.",
  bad_code: "That code didn't work. Check it, or ask for a new one.",
  unknown: "Something went wrong. Try again in a moment.",
};
